/**
 * Tab „Anträge“ (/urlaub): Antragsliste mit Status-Badges + Filter
 * (Status/Mitarbeiter/Jahr) und Workflow-Aktionen:
 * Genehmigen/Ablehnen (Gate personal_schreiben, Dialog mit Begruendung),
 * Stornieren (PENDING sofort; APPROVED → „Storno anfragen“ bzw. Bestaetigung
 * durch personal_schreiben — Logik serverseitig, AP 3.2).
 */

import { useMemo, useState } from "react"
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Check, Plus, Undo2, X } from "lucide-react"
import { toast } from "sonner"
import { LoadingSpinner } from "@/components/shared"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { DataGrid, type ColumnDef } from "@/components/ui/data-grid"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { ABSENCE_STATUS_BADGE, ABSENCE_STATUS_LABEL } from "@/lib/absenceColors"
import { fmtDate, fmtDays } from "@/lib/format"
import { useCan } from "@/lib/permissions"
import { api, apiError } from "@/services/api"
import type { AbsenceStatus, LeaveRequest } from "@/types"
import { VacationRequestDialog } from "./VacationRequestDialog"

type DecisionMode = "approve" | "reject" | "cancel"

const DECISION_TEXT: Record<
  DecisionMode,
  { title: string; description: string; confirm: string }
> = {
  approve: {
    title: "Antrag genehmigen",
    description: "Der Antrag wird genehmigt; die Tage werden dem Konto angerechnet.",
    confirm: "Genehmigen",
  },
  reject: {
    title: "Antrag ablehnen",
    description: "Der Zeitraum wird wieder frei. Eine Begründung ist Pflicht.",
    confirm: "Ablehnen",
  },
  cancel: {
    title: "Antrag stornieren",
    description:
      "Beantragte Anträge werden sofort storniert; bei genehmigten Anträgen wird " +
      "ohne Personal-Berechtigung nur ein Storno angefragt.",
    confirm: "Stornieren",
  },
}

function DecisionDialog({
  mode,
  request,
  onClose,
}: {
  mode: DecisionMode
  request: LeaveRequest
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [note, setNote] = useState("")
  const text = DECISION_TEXT[mode]

  const mutate = useMutation({
    mutationFn: () => {
      const trimmed = note.trim()
      if (mode === "approve")
        return api.leave.requests.approve(request.id, trimmed ? { note: trimmed } : undefined)
      if (mode === "reject") return api.leave.requests.reject(request.id, trimmed)
      return api.leave.requests.cancel(request.id, trimmed ? { note: trimmed } : undefined)
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["leave"] })
      queryClient.invalidateQueries({ queryKey: ["absences"] })
      if (mode === "cancel" && result.cancellationRequested) {
        toast.success("Storno angefragt — wartet auf Bestätigung durch die Personalabteilung")
      } else {
        toast.success(
          mode === "approve"
            ? "Antrag genehmigt"
            : mode === "reject"
              ? "Antrag abgelehnt"
              : "Antrag storniert"
        )
      }
      onClose()
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const valid = mode !== "reject" || note.trim() !== ""

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{text.title}</DialogTitle>
          <DialogDescription>
            {fmtDate(request.startDate)} – {fmtDate(request.endDate)} (
            {fmtDays(request.requestedDays)} Tage). {text.description}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 py-2">
          <Label>Begründung {mode === "reject" ? "*" : "(optional)"}</Label>
          <Textarea
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={mode === "reject" ? "Warum wird der Antrag abgelehnt?" : ""}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant={mode === "approve" ? "default" : "destructive"}
            disabled={!valid || mutate.isPending}
            onClick={() => mutate.mutate()}
          >
            {text.confirm}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function LeaveRequestsTab() {
  const canWrite = useCan("personal_schreiben")
  const currentYear = new Date().getFullYear()

  const [statusFilter, setStatusFilter] = useState<AbsenceStatus | "">("")
  const [employeeFilter, setEmployeeFilter] = useState("")
  const [yearFilter, setYearFilter] = useState(String(currentYear))
  const [requestOpen, setRequestOpen] = useState(false)
  const [decision, setDecision] = useState<{ mode: DecisionMode; request: LeaveRequest } | null>(
    null
  )

  const employees = useQuery({
    queryKey: ["employees", { size: 100 }],
    queryFn: () => api.employees.list({ size: 100 }),
  })
  const employeeName = (id: string | null) =>
    id === null
      ? "—"
      : (employees.data?.data.find((e) => e.id === id)?.displayName ?? `Mitarbeiter #${id}`)

  const params = {
    size: 100,
    status: statusFilter || undefined,
    employee: employeeFilter ? Number(employeeFilter) : undefined,
    year: yearFilter ? Number(yearFilter) : undefined,
  }
  const query = useQuery({
    queryKey: ["leave", "requests", params],
    queryFn: () => api.leave.requests.list(params),
    placeholderData: keepPreviousData,
  })
  const rows = query.data?.data ?? []

  /** Aktionen je Zeile — Sichtbarkeit ist nur UI-Komfort, durchgesetzt wird serverseitig */
  function rowActions(request: LeaveRequest) {
    const actions: { label: string; icon: React.ReactNode; mode: DecisionMode }[] = []
    if (request.status === "PENDING") {
      if (canWrite) {
        actions.push({ label: "Genehmigen", icon: <Check />, mode: "approve" })
        actions.push({ label: "Ablehnen", icon: <X />, mode: "reject" })
      }
      actions.push({ label: "Stornieren", icon: <Undo2 />, mode: "cancel" })
    } else if (request.status === "APPROVED") {
      if (canWrite) {
        actions.push({
          label: request.cancellationRequested ? "Storno bestätigen" : "Stornieren",
          icon: <Undo2 />,
          mode: "cancel",
        })
      } else if (!request.cancellationRequested) {
        actions.push({ label: "Storno anfragen", icon: <Undo2 />, mode: "cancel" })
      }
    }
    return actions
  }

  // Grid-Spalten: header als String, damit die Spalten-Auswahl ihn anzeigen kann
  const columns = useMemo<ColumnDef<LeaveRequest>[]>(
    () => [
      {
        id: "employee",
        header: "Antragsteller",
        accessorFn: (r) => employeeName(r.employeeId),
        meta: { cellClassName: "font-medium" },
      },
      {
        id: "period",
        header: "Zeitraum",
        // ISO-Startdatum sortiert chronologisch korrekt
        accessorFn: (r) => r.startDate,
        cell: ({ row }) => {
          const r = row.original
          return (
            <>
              {fmtDate(r.startDate)}
              {r.halfDayStart && " (½)"} – {fmtDate(r.endDate)}
              {r.halfDayEnd && " (½)"}
            </>
          )
        },
        meta: { cellClassName: "tabular-nums" },
      },
      {
        id: "days",
        header: "Tage",
        accessorFn: (r) => Number(r.requestedDays),
        cell: ({ row }) => fmtDays(row.original.requestedDays),
        meta: { headClassName: "text-right", cellClassName: "text-right tabular-nums" },
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (r) => ABSENCE_STATUS_LABEL[r.status],
        cell: ({ row }) => {
          const r = row.original
          return (
            <div className="flex flex-col items-start gap-1">
              <Badge variant="outline" className={ABSENCE_STATUS_BADGE[r.status]}>
                {ABSENCE_STATUS_LABEL[r.status]}
              </Badge>
              {r.cancellationRequested && r.status === "APPROVED" && (
                <span className="text-xs text-amber-700">Storno angefragt</span>
              )}
            </div>
          )
        },
      },
      {
        id: "substitute",
        header: "Vertretung",
        accessorFn: (r) => employeeName(r.substituteEmployeeId),
        meta: { cellClassName: "text-muted-foreground" },
      },
      {
        id: "note",
        header: "Notiz",
        accessorFn: (r) => r.decisionNote ?? r.note ?? "",
        cell: ({ row }) => {
          const r = row.original
          return (
            <span title={r.decisionNote ?? r.note ?? undefined}>
              {r.decisionNote ?? r.note ?? "—"}
            </span>
          )
        },
        meta: { cellClassName: "max-w-[16rem] truncate text-muted-foreground" },
      },
      {
        id: "aktionen",
        header: "Aktionen",
        enableSorting: false,
        enableHiding: false,
        meta: { headClassName: "text-right", cellClassName: "text-right" },
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            {rowActions(row.original).map((a) => (
              <Button
                key={a.mode + a.label}
                variant={a.mode === "approve" ? "default" : "outline"}
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setDecision({ mode: a.mode, request: row.original })}
              >
                {a.icon} {a.label}
              </Button>
            ))}
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps — employeeName/rowActions hängen an employees.data/canWrite
    [employees.data, canWrite],
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        {/* Filterleiste */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-44 space-y-1.5">
            <Label>Status</Label>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as AbsenceStatus | "")}
            >
              <option value="">Alle Status</option>
              {(["PENDING", "APPROVED", "REJECTED", "CANCELLED"] as AbsenceStatus[]).map((s) => (
                <option key={s} value={s}>
                  {ABSENCE_STATUS_LABEL[s]}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-56 space-y-1.5">
            <Label>Mitarbeiter</Label>
            <Select value={employeeFilter} onChange={(e) => setEmployeeFilter(e.target.value)}>
              <option value="">Alle Mitarbeiter</option>
              {(employees.data?.data ?? []).map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.displayName}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-32 space-y-1.5">
            <Label>Jahr</Label>
            <Select value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
              <option value="">Alle Jahre</option>
              {[currentYear - 1, currentYear, currentYear + 1, currentYear + 2].map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <Button onClick={() => setRequestOpen(true)}>
          <Plus /> Urlaub beantragen
        </Button>
      </div>

      <Card>
        <CardContent className="py-2">
          {query.isLoading ? (
            <LoadingSpinner fullPage />
          ) : (
            <DataGrid
              columns={columns}
              data={rows}
              storageKey="leave-requests"
              emptyMessage="Keine Urlaubsanträge gefunden."
            />
          )}
        </CardContent>
      </Card>

      <VacationRequestDialog open={requestOpen} onOpenChange={setRequestOpen} />
      {decision && (
        <DecisionDialog
          mode={decision.mode}
          request={decision.request}
          onClose={() => setDecision(null)}
        />
      )}
    </div>
  )
}
